import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlarmClock, ArrowRight, CalendarClock, Activity, Plus, Sun, Sunrise, Sunset, Snowflake,
} from "lucide-react";
import {
  Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { api } from "@/lib/api";
import { fdate, money, relative } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { StatCard } from "@/components/common/StatCard";
import { StatusBadge } from "@/components/common/StatusBadge";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { UserAvatar } from "@/components/common/UserAvatar";
import { cn } from "@/lib/utils";

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return { text: "Good morning", Icon: Sunrise };
  if (h < 17) return { text: "Good afternoon", Icon: Sun };
  return { text: "Good evening", Icon: Sunset };
};

const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 shadow-md">
      <p className="text-xs font-semibold">{label}</p>
      <p className="num text-sm text-primary">{payload[0].value} leads</p>
    </div>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [stale, setStale] = useState([]);
  const [loading, setLoading] = useState(true);
  const { text: hello, Icon: HelloIcon } = greeting();

  useEffect(() => {
    api.get("/dashboard")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
    api.get("/leads/stale", { params: { days: 7 } })
      .then((r) => setStale(r.data))
      .catch(() => {});
  }, []);

  const pipeline = useMemo(
    () => (data?.pipeline || []).filter((p) => p.count > 0).sort((a, b) => b.count - a.count).slice(0, 7),
    [data]
  );

  const todayStr = new Date().toISOString().slice(0, 10);
  const overdue = (data?.followups || []).filter((f) => f.date < todayStr);
  const dueToday = (data?.followups || []).filter((f) => f.date === todayStr);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24 w-full rounded-xl" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-7">
      {/* Greeting banner */}
      <div className="grain relative overflow-hidden rounded-2xl border border-border bg-gradient-panel p-6 sm:p-8">
        <div className="pointer-events-none absolute -right-16 -top-24 h-64 w-64 rounded-full bg-primary/10 blur-3xl" />
        <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-4">
            <UserAvatar key={user?.email} name={user?.name} src={user?.avatar} size="lg" ring />
            <div>
              <p className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
                <HelloIcon className="h-4 w-4 text-primary" /> {hello}
              </p>
              <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{user?.name}</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                {user?.designation || "Team member"}
                {user?.department ? ` . ${user.department}` : ""} . {fdate(new Date())}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => navigate("/app/crm?new=1")} data-testid="dash-new-lead">
              <Plus className="h-4 w-4" /> New Lead
            </Button>
            <Button variant="outline" onClick={() => navigate("/app/crm?tab=followups")}>
              <CalendarClock className="h-4 w-4" /> Follow-ups
            </Button>
          </div>
        </div>
      </div>

      {/* Attention strip */}
      {(overdue.length > 0 || dueToday.length > 0) && (
        <div className="grid gap-3 sm:grid-cols-2">
          {overdue.length > 0 && (
            <button
              onClick={() => navigate("/app/crm?tab=followups&bucket=overdue")}
              className="flex items-center gap-3 rounded-xl border border-destructive/25 bg-destructive-soft px-4 py-3 text-left transition-transform hover:-translate-y-0.5"
            >
              <AlarmClock className="h-5 w-5 shrink-0 text-destructive" />
              <span className="flex-1">
                <span className="block text-sm font-semibold text-destructive">{overdue.length} overdue follow-up{overdue.length > 1 ? "s" : ""}</span>
                <span className="block text-xs text-foreground/70">These customers are still waiting to hear from you.</span>
              </span>
              <ArrowRight className="h-4 w-4 text-destructive" />
            </button>
          )}
          {dueToday.length > 0 && (
            <button
              onClick={() => navigate("/app/crm?tab=followups&bucket=today")}
              className="flex items-center gap-3 rounded-xl border border-warning/30 bg-warning-soft px-4 py-3 text-left transition-transform hover:-translate-y-0.5"
            >
              <CalendarClock className="h-5 w-5 shrink-0 text-warning" />
              <span className="flex-1">
                <span className="block text-sm font-semibold text-warning">{dueToday.length} follow-up{dueToday.length > 1 ? "s" : ""} due today</span>
                <span className="block text-xs text-foreground/70">Clear them before the day ends.</span>
              </span>
              <ArrowRight className="h-4 w-4 text-warning" />
            </button>
          )}
        </div>
      )}

      {/* Monthly target */}
      {data?.target?.target > 0 && (
        <Card data-testid="target-card">
          <CardContent className="py-5">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Monthly target . {data.target.month}
                </p>
                <p className="num mt-1 text-2xl font-bold">
                  {money(data.target.achieved)}
                  <span className="ml-1 text-sm font-medium text-muted-foreground">of {money(data.target.target)}</span>
                </p>
              </div>
              <div className="text-right">
                <p className="num text-2xl font-bold text-primary">{data.target.percent}%</p>
                <p className="text-xs text-muted-foreground">{data.target.days_left} days left</p>
              </div>
            </div>
            <div className="mt-4 h-3 overflow-hidden rounded-full bg-muted">
              <div className="h-full rounded-full bg-gradient-warm transition-all duration-700"
                style={{ width: `${Math.min(100, data.target.percent)}%` }} />
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              {data.target.percent >= 100
                ? "Target achieved. Excellent work."
                : `${money(Math.max(0, data.target.target - data.target.achieved))} to go.`}
            </p>
          </CardContent>
        </Card>
      )}

      {/* KPI cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {(data?.cards || []).map((c, i) => (
          <StatCard key={c.key} index={i} label={c.label} value={c.value} icon={c.icon} tone={c.tone} currency={c.currency} />
        ))}
      </div>

      <div className="grid gap-5 lg:grid-cols-5">
        {/* Pipeline */}
        <Card className="lg:col-span-3">
          <CardHeader className="pb-2">
            <CardTitle className="font-display text-base">Lead pipeline</CardTitle>
            <p className="text-sm text-muted-foreground">Where your leads currently stand.</p>
          </CardHeader>
          <CardContent>
            {pipeline.length === 0 ? (
              <EmptyState title="No leads yet" description="Create your first lead to see the pipeline build up here."
                action={<Button onClick={() => navigate("/app/crm?new=1")}><Plus className="h-4 w-4" /> Add lead</Button>} />
            ) : (
              <div className="h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={pipeline} margin={{ top: 8, right: 8, left: -18, bottom: 44 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="status" angle={-28} textAnchor="end" interval={0}
                      tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                    <Tooltip content={<ChartTip />} cursor={{ fill: "hsl(var(--primary) / 0.06)" }} />
                    <Bar dataKey="count" radius={[6, 6, 0, 0]} maxBarSize={46}>
                      {pipeline.map((_, i) => (
                        <Cell key={i} fill={`hsl(var(--chart-${(i % 5) + 1}))`} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming follow-ups */}
        <Card className="flex flex-col lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="font-display text-base">Next follow-ups</CardTitle>
            <p className="text-sm text-muted-foreground">Your nearest scheduled conversations.</p>
          </CardHeader>
          <CardContent className="flex flex-1 flex-col">
            {(data?.followups || []).length === 0 ? (
              <EmptyState icon={CalendarClock} title="Nothing scheduled" description="You are all caught up." />
            ) : (
              <div className="space-y-2.5">
                {data.followups.slice(0, 6).map((f) => (
                  <div key={f.id} className="flex items-start gap-3 rounded-lg border border-border p-3 transition-colors hover:border-primary/30">
                    <span className={cn(
                      "mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-lg text-[11px] font-bold",
                      f.date < todayStr ? "bg-destructive-soft text-destructive"
                        : f.date === todayStr ? "bg-warning-soft text-warning" : "bg-info-soft text-info"
                    )}>
                      {new Date(f.date).getDate()}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold">{f.customer_name}</p>
                      <p className="truncate text-xs text-muted-foreground">{f.next_action || f.remarks}</p>
                    </div>
                    <span className="whitespace-nowrap text-[11px] font-medium text-muted-foreground">{fdate(f.date)}</span>
                  </div>
                ))}
              </div>
            )}
            <Button variant="ghost" className="mt-auto w-full justify-center pt-3 text-primary"
              onClick={() => navigate("/app/crm?tab=followups")}>
              View all follow-ups <ArrowRight className="h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Leads going quiet */}
      {stale.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <CardTitle className="font-display text-base">Going quiet</CardTitle>
                <p className="text-sm text-muted-foreground">
                  No follow-up scheduled and no activity for a week. A quick call here usually pays off.
                </p>
              </div>
              <span className="num rounded-full bg-warning-soft px-2.5 py-1 text-xs font-bold text-warning">
                {stale.length} lead{stale.length > 1 ? "s" : ""}
              </span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
              {stale.slice(0, 6).map((l) => (
                <button
                  key={l.id}
                  onClick={() => navigate(`/app/crm?open=${l.id}`)}
                  data-testid={`stale-${l.lead_no}`}
                  className="flex items-center gap-3 rounded-lg border border-border p-3 text-left transition-all hover:-translate-y-0.5 hover:border-warning/50 hover:shadow-sm"
                >
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-warning-soft text-warning">
                    <Snowflake className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold">{l.customer_name}</span>
                    <span className="block truncate text-xs text-muted-foreground">{l.company || l.phone}</span>
                  </span>
                  <span className="num whitespace-nowrap text-xs font-medium text-warning">{l.days_idle}d</span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent activity */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="font-display text-base">Recent activity</CardTitle>
          <p className="text-sm text-muted-foreground">
            {user?.role === "admin" ? "Everything happening across the company." : "Your latest actions in the system."}
          </p>
        </CardHeader>
        <CardContent>
          {(data?.recent || []).length === 0 ? (
            <EmptyState icon={Activity} title="No activity yet" description="Actions you take will be recorded here." />
          ) : (
            <ol className="relative space-y-4 border-l border-border pl-5">
              {data.recent.map((r) => (
                <li key={r.id || r.created_at} className="relative">
                  <span className="absolute -left-[26px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-background bg-primary" />
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <p className="text-sm font-medium">{r.record || r.customer_name}</p>
                    {r.ref && <StatusBadge status={r.ref} tone="neutral" dot={false} className="text-[10px]" />}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {r.user_name || r.created_by_name} . {relative(r.created_at)}
                  </p>
                </li>
              ))}
            </ol>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
